import { Component, Inject, Input, LOCALE_ID, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { LinqChartData } from '../linq-chart-data';
import { ChartConfiguration, ChartType, Tooltip } from 'chart.js';
import { Chart } from 'chart.js';
import {TreemapController, TreemapElement} from 'chartjs-chart-treemap';
import { formatCurrency, formatNumber } from '@angular/common';
import { Currency, DEFAULT_CURRENCY, getCurrency } from '../../common/organisation';
import { OrganisationService } from '../../organisation/organisation.service';
import { abbreviate } from '../bar-chart/bar-chart.component';

Chart.register(Tooltip, TreemapController, TreemapElement);

const INK = '#1E2233'
const FONT_FAMILY = "'Montserrat', 'Roboto', system-ui, sans-serif"
// the tiles' colour when none is given: the app's teal
const DEFAULT_COLOUR = '#00A9A5'

// a colour as red, green and blue, from #rrggbb or a few names; undefined when it can't be read
const toRgb = (colour:string): [number, number, number] | undefined => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec((colour || '').trim())
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : undefined
}

// the colour mixed towards white: 0 is the colour itself, 1 is white
const lighten = ([r, g, b]:[number, number, number], amount:number) =>
    `rgb(${Math.round(r + (255 - r) * amount)}, ${Math.round(g + (255 - g) * amount)}, ${Math.round(b + (255 - b) * amount)})`

// whether text on the colour should be dark
const isLight = ([r, g, b]:[number, number, number]) => 0.299 * r + 0.587 * g + 0.114 * b > 165

@Component({
  selector: 'app-treemap',
  templateUrl: 'treemap.html',
  styleUrl: 'treemap.css',
  standalone: false
})
export class Treemap implements OnInit, OnChanges {
    @Input() data: LinqChartData[]
    @Input() legend: boolean = false
    @Input() isCurrency: boolean = false
    @Input() colour: string                     // one colour for every tile, deepest for the largest
    @Input() height = 320

    public chartOptions: ChartConfiguration['options']
    public chartType: ChartType = 'treemap';
    public chartData:any

    currency:Currency

    constructor(@Inject(LOCALE_ID) public locale: string, organisationService:OrganisationService) {
        this.currency = getCurrency(organisationService.organisation?.currencyCode||DEFAULT_CURRENCY)
    }

    ngOnInit(): void {
        this.configure()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes["data"] || changes["colour"]) && this.data) {
            this.configure()
            this.setData()
        }
    }

    get style() {
        return `position: relative; width: 100%; height: ${this.height}px;`
    }

    private format(value:number, short:boolean) {
        if (this.isCurrency) return short ? this.currency.symbol + abbreviate(value, this.locale) : formatCurrency(value, this.locale, this.currency.symbol, this.currency.code, '1.0-0')
        return short ? abbreviate(value, this.locale) : formatNumber(value, this.locale)
    }

    configure() {
        this.chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: { display: false },
                legend: { display: false },
                // the tiles carry their own labels; the value-label plugin other charts take through [plugins] isn't given
                // to this one, and this keeps the tiles clear should it ever be registered for every chart
                datalabels: { display: false },
                tooltip: {
                    enabled: true,
                    backgroundColor: INK,
                    padding: 10,
                    cornerRadius: 6,
                    titleFont: { family: FONT_FAMILY, size: 12, weight: 600 },
                    bodyFont: { family: FONT_FAMILY, size: 12 },
                    callbacks: {
                        title: (items:any[]) => items.map((i:any) => i.raw._data.name).join(", "),
                        label: (ctx:any) => this.format(ctx.raw.v, false),
                    }
                }
            }
        } as any
    }

    setData() {
        // largest first, so the shades run from deep (largest) to light (smallest)
        const values = (this.data || []).map(d => Number(d?.value) || 0)
        const max = Math.max(0, ...values)
        const base = toRgb(this.colour) || toRgb(DEFAULT_COLOUR)!
        // a tile's shade: by its share of the largest (square-rooted, so the small ones aren't all the palest)
        const shade = (v:number) => lighten(base, max > 0 ? 0.72 * (1 - Math.sqrt(Math.max(0, v) / max)) : 0.5)
        const textOn = (v:number) => {
            const rgb = /rgb\((\d+), (\d+), (\d+)\)/.exec(shade(v))
            return rgb && isLight([+rgb[1], +rgb[2], +rgb[3]]) ? INK : '#ffffff'
        }

        this.chartData = {datasets:[
            {
                tree: this.data,
                key: 'value',
                borderWidth: 0,
                borderRadius: 4,
                spacing: 2,
                backgroundColor: (ctx:any) => ctx.type !== 'data' ? 'transparent' : shade(ctx.raw.v),
                hoverBackgroundColor: (ctx:any) => ctx.type !== 'data' ? 'transparent' : lighten(base, 0),
                labels: {
                    display: true,
                    align: 'left',
                    position: 'top',
                    // a tile too small for its name shows nothing: hover names it
                    overflow: 'hidden',
                    padding: 6,
                    formatter: (ctx:any) => ctx.type !== 'data' ? '' : [ctx.raw._data.name, this.format(ctx.raw.v, true)],
                    color: (ctx:any) => ctx.type !== 'data' ? INK : textOn(ctx.raw.v),
                    font: [{ family: FONT_FAMILY, size: 12, weight: 600 }, { family: FONT_FAMILY, size: 11 }],
                }
            }
        ]}
    }
}
